import { LitElement, html, nothing } from "lit";
import { until } from "lit/directives/until.js";

import { SimpleRouterMixin } from './mixins/simple-router-mixin';
import { ROLE_CLIENT, ROLE_TRAINER } from './constants';
import './components/progress-bar';
import './navigation';

customElements.define(
  "ks-booking-shell",
  class extends SimpleRouterMixin(LitElement) {
    static get properties() {
      return {
        _progress: { state: true },
        _user: {state: true},
        tokenValue: {},
        api: {},
        config: {},
        theme: {reflect: true},
      };
    }

    #abortController = new AbortController();
    #userPromise;

    createRenderRoot() {
      return this;
    }

    connectedCallback() {
      super.connectedCallback();

      const { signal } = this.#abortController; 
      this.#retrieveLoggedInUser();
      this.addEventListener('login', () => {
          this.#retrieveLoggedInUser();
      }, { signal });
    }

    disconnectedCallback() {
      this.#abortController.abort();
    }

    render() {
      return html`
        ${!this.theme
          ? html`
              <header>
                <div class="container">
                  <ks-booking-navigation
                    .featureBasePath=${this.featureBasePath}
                    .tokenValue=${this.tokenValue}
                    @logout=${(e) => {this.dispatchEvent(new Event(e.type))}}
                  ></ks-booking-navigation>
                </div>
              </header>
            `
          : nothing}
        <main>
          <ks-progress-bar ?active=${this._progress}></ks-progress-bar>
          ${this._routeTemplate}
        </main>
      `;
    }

    /**
     * Returns an array of routes to be used.
     *
     * @returns {Array<Object>}
     */
    _setupRoutes() {
      return [
        {
          id: 'shell-authentication',
          pattern: new URLPattern({
            pathname: `${this.featureBasePath}authentication/*`,
          }),
          handler: async () => {
            document.title = 'Kasorb training booking authentication';
            return html`${until(
              import('./pages/authentication.js').then((foo) => {
                return this.#renderAuthenticationRoute();
              }),
              nothing,
            )}`
          },
        },
        {
          id: 'shell-dashboard',
          pattern: new URLPattern({
            pathname: `${this.featureBasePath}dashboard/*`,
          }),
          handler: async () => {
            requestAnimationFrame(() => {
              this.navigation.navigate(`${this.featureBasePath}requests/`);
            });
          },
        },
        {
          id: 'shell-requests',
          pattern: new URLPattern({
            pathname: `${this.featureBasePath}requests/*`,
          }),
          handler: this.#privateRoute(async () => {
            document.title = 'Kasorb training booking requests';
            // Wait for the logged in user to be known before rendering the
            // requests flow, so it can be handed down instead of refetched.
            return html`${until(
              Promise.all([
                import('./pages/requests.js'),
                this.#userPromise ?? this.#retrieveLoggedInUser(),
              ]).then(() => {
                return this.#renderRequestsRoute();
              }),
              nothing,
            )}`
          }),
        },
        {
          id: 'shell-calendar',
          pattern: new URLPattern({
            pathname: `${this.featureBasePath}calendar/:year/:month/`,
          }),
          handler: async ({ params }) => {
            const month = Number.parseInt(params.month);
            const year = Number.parseInt(params.year);
            if (month < 1 || month > 12 || year < 0) {
              // TODO: redirect to current date
              return nothing;
            }
            const date = new Date(year, month - 1);
            document.title = 'Kasorb training booking calendar';
            return html`${until(
              import('./pages/calendar').then((foo) => {
                return this.#renderCalendarRoute({ date });
              }),
              nothing,
            )}`
          },
        },
        {
          pattern: new URLPattern({
            pathname: `${this.featureBasePath}calendar/*`,
          }),
          handler: async () => {
            const date = new Date();
            const [month, year] = new Intl.DateTimeFormat("en-GB", {
              year: "numeric",
              month: "2-digit",
            })
              .formatToParts(date)
              .filter((part) => ["year", "month"].includes(part.type))
              .map((part) => part.value);
            setTimeout(() => {
              this.navigation.navigate(
                `${this.featureBasePath}calendar/${year}/${month}/`,
              );
            }, 0);
          },
        },
        {
          id: 'shell-checkout',
          pattern: new URLPattern({
            pathname: `${this.featureBasePath}checkout/*`,
          }),
          handler: async () => {
            document.title = 'Kasorb training booking checkout';
            return html`${until(
              import('./pages/checkout.js').then((foo) => {
                return this.#renderCheckoutRoute();
              }),
              nothing,
            )}`
          },
        },
        // Empty route
        {
          id: 'shell-empty',
          pattern: new URLPattern({
            pathname: `${this.featureBasePath}*`,
          }),
          handler: async () => {
            requestAnimationFrame(() => {
              this.navigation.navigate(`${this.featureBasePath}dashboard/`);
            });
          },
        }
      ];
    }

    #privateRoute(handler) {
      return () => {
        if (!this.tokenValue) {
          requestAnimationFrame(() => {
            this.navigation.navigate(`${this.featureBasePath}authentication/`);
          });
          return nothing;
        }
        return handler();
      }
    }

    #renderAuthenticationRoute() {
      return html`
        <ks-page-authentication
          .featureBasePath=${`${this.featureBasePath}authentication/`}
          .api=${this.api}
          @success=${(e) => {
            this.tokenValue = e.detail;
            this.dispatchEvent(new CustomEvent('login', { detail: e.detail }));
            this.navigation.navigate(`${this.featureBasePath}dashboard/`);
          }}
        ></ks-page-authentication>
      `;
    }

    #renderRequestsRoute() {
      return html`
        <ks-page-requests
          .featureBasePath=${`${this.featureBasePath}requests/`}
          .tokenValue=${this.tokenValue}
          .api=${this.api}
          .config=${this.config}
          .user=${this._user}
          @fetch-booking-requests:load=${() => {
            this._progress = true;
          }}
          @fetch-booking-requests:completed=${() => {
            this._progress = false;
          }}
        ></ks-page-requests>
      `;
    }

    /**
     * Defines the calendar route template.
     *
     * @private
     * @returns {TemplateResult}
     */
    async #renderCalendarRoute({ date }) {
      const data = this.api
        .retrieveEventsCalendar({
          month: date.getMonth() + 1,
          year: date.getFullYear(),
        });

      // Check if we have stamped the template previously.
      // If so, then this cannot be the initial load.
      const initialLoad =
        this.renderRoot.querySelector("ks-page-calendar") === null;

      const renderTemplate = (date, events) => html`
        <ks-page-calendar
         id="calendar-page"
         .currentDate=${date}
         .events=${events}
         @cursor-date:change=${(e) => {
           const date = e.detail;
           const [month, year] = new Intl.DateTimeFormat("en-GB", {
             year: "numeric",
             month: "2-digit",
           })
             .formatToParts(date)
             .filter((part) => ["year", "month"].includes(part.type))
             .map((part) => part.value);
           const path = `${this.featureBasePath}calendar/${year}/${month}/`;
           this.navigation.navigate(path);
           this._refreshCurrentRoute();
         }}
        >
        </ks-page-calendar>
      `;

      // Don't display anything if this is the initial load and no events are loaded.
      if (initialLoad) {
        return html`${until(
          data.then((events) => {
            return renderTemplate(date, events);
          }),
          nothing,
        )}`;
      }

      // Don't clean screen while data loads. Allow data to
      // be updated on the old screen when it comes through.
      const events = await data;
      return renderTemplate(date, events);
    }

    #renderCheckoutRoute() {
      return html`
        <ks-page-checkout
          .featureBasePath=${`${this.featureBasePath}checkout/`}
          .api=${this.api}
          .config=${this.config}
          .partner=${this.theme}
        ></ks-page-checkout>
      `;
    }

    #retrieveLoggedInUser() {
      this.#userPromise = this.#loadLoggedInUser();
      return this.#userPromise;
    }

    async #loadLoggedInUser() {
      if (!this.tokenValue) {
        return;
      }
      this._user = await this.api.retrieveLoggedInUser();
      if (this._user.role !== ROLE_CLIENT) {
        this.dispatchEvent(new Event('logout'));
        if (this._user.role === ROLE_TRAINER) {
          window.location.href = this.config.trainingAppURL;
        } else {
          window.location.href = this.config.publicWebsiteURL;
        }
        return;
      }
      this.dispatchEvent(new CustomEvent('user', { detail: this._user }));
      return this._user;
    }

  }
);
